import { PulseBaseProvider } from '../../base-classes';

const _currentAirQualityCacheKey = 'currentAirQuality';
const _forecastAirQualityCacheKey = 'forecastAirQuality';
const _currentAirQualityUri='/api/aqi/v4';
const _forecastAirQualityUri = '/api/aqi/v4/forecast';

let _hours = 8;

export class AirQualityProvider extends PulseBaseProvider {
    constructor(baseUrl, authId, secret, options, logger) {
        super(baseUrl, authId, secret, options, logger);
        this.storage.sync[_currentAirQualityCacheKey] = this.currentAirQualitySyncer.bind(this);
        this.storage.sync[_forecastAirQualityCacheKey] = this.forecastAirQualitySyncer.bind(this);
    }

    async currentAirQualitySyncer(params) {
        const { id, syncParams, resolve, reject } = params;
        let queryParams = {
            ...syncParams.stdParameters,
            locationType: 'latitudelongitude',
            location: `${syncParams.latitude},${syncParams.longitude}`,
            cachedOnly: syncParams.isCached,
            verbose: syncParams.verbose
        };
        return await super.syncData(syncParams.key, id, queryParams, _currentAirQualityUri, resolve, reject, null, syncParams.pulseApi);
    }

    async getCurrentAirQuality(latitude, longitude, isCached, stdParameters, verbose) {
        const syncParams = {
            latitude,
            longitude,
            verbose: !!verbose,
            isCached
        };
        const id = JSON.stringify(syncParams);

        //Add the standard parameters after generating the ID because they shouldn't be part of the cache key
        syncParams.stdParameters = stdParameters;
        return await super.loadFromStorage(_currentAirQualityCacheKey, id, syncParams);
    }

    async forecastAirQualitySyncer(params) {
        const { id, syncParams, resolve, reject } = params;

        let queryParams = {
            ...syncParams.stdParameters,
            locationType: 'latitudelongitude',
            location: `${syncParams.latitude},${syncParams.longitude}`,
            isCached: syncParams.isCached,
            hours: syncParams.hours,
            verbose: syncParams.verbose,
            useCache: syncParams.useCache
        };

        return await super.syncData(syncParams.key, id, queryParams, _forecastAirQualityUri, resolve, reject, undefined, syncParams.pulseApi);
    }

    async getAirQualityForecast(latitude, longitude, stdParameters, verbose, useCache) {
        const syncParams = {
            latitude,
            longitude,
            hours: _hours,
            verbose: !!verbose,
            isCached: false,
            useCache
        };

        const id =  JSON.stringify(syncParams);

        //Add the standard parameters after generating the ID because they shouldn't be part of the cache key
        syncParams.stdParameters = stdParameters;

        return await super.loadFromStorage(_forecastAirQualityCacheKey, id, syncParams);
    }

    async refreshCurrentAirQuality(latitude, longitude, stdParameters, verbose) {
        const key = _currentAirQualityCacheKey;
        const syncParams = {
            latitude,
            longitude,
            verbose: !!verbose,
            isCached: false
        };
        const id =  JSON.stringify(syncParams);
        this.storage.remove({
            key,
            id
        });

        return await this.getCurrentAirQuality(latitude, longitude, false, stdParameters);
    }

    async refreshAirQualityForecast(latitude, longitude, stdParameters, verbose, useCache) {
        const key = _forecastAirQualityCacheKey;
        const syncParams = {
            latitude,
            longitude,
            hours: _hours,
            verbose: !!verbose,
            isCached: false,
            useCache
        };
        const id =  JSON.stringify(syncParams);
        this.storage.remove({
            key,
            id
        });
        return await this.getAirQualityForecast(latitude, longitude, stdParameters, verbose, useCache);
    }

    getPollutantTypes() {
        return [
            {
                type: 'PM2.5',
                name: 'Fine particulate matter',
                safeMax: '2.5μm',
                details: [
                    'Main sources are fuel burning processes in industry, transportation and indoor heating',
                    'Particules enter the lungs and cause local and systemic inflammation in the resipratory system & heart, thus cause cardiovascular and respiratory diseases such as asthma and bronchitis'
                ],
                unit: 'μg/m\u00B3',
                apiField: 'pm25',
                qualityRanges: [
                    {
                        min: 350.5,
                        max: 500.4,
                        gaugeFill: 100,
                        description: 'Hazardous'
                    },
                    {
                        min: 250.5,
                        max: 350.4,
                        gaugeFill: 90,
                        description: 'Hazardous'
                    },
                    {
                        min: 150.5,
                        max: 250.4,
                        gaugeFill: 80,
                        description: 'Very Unhealthy'
                    },
                    {
                        min: 55.5,
                        max: 150.4,
                        gaugeFill: 75,
                        description: 'Unhealthy'
                    },
                    {
                        min: 35.5,
                        max: 55.4,
                        gaugeFill: 50,
                        description: 'Unhealthy or Sensitive Groups'
                    },
                    {
                        min: 12.1,
                        max: 35.4,
                        gaugeFill: 30,
                        description: 'Moderate'
                    },
                    {
                        min: 0,
                        max: 12.0,
                        gaugeFill: 19,
                        description: 'Good'
                    }
                ]
            },
            {
                type: 'PM10',
                name: 'Inhalable particulate matter',
                safeMax: '10μm',
                details: [
                    'Main sources are natural dust, smoke and pollen',
                    'Cause local and systemic inflammation in the respiratory system & heart',
                    'Cause cardiovascular and respiratory diseases such as asthma and bronchitis'
                ],
                unit: 'μg/m\u00B3',
                apiField: 'pm10',
                qualityRanges: [
                    {
                        min: 505,
                        max: 604,
                        gaugeFill: 100,
                        description: 'Hazardous'
                    },
                    {
                        min: 425,
                        max: 504,
                        gaugeFill: 90,
                        description: 'Hazardous'
                    },
                    {
                        min: 355,
                        max: 424,
                        gaugeFill: 80,
                        description: 'Very Unhealthy'
                    },
                    {
                        min: 255,
                        max: 354,
                        gaugeFill: 75,
                        description: 'Unhealthy'
                    },
                    {
                        min: 155,
                        max: 254,
                        gaugeFill: 50,
                        description: 'Unhealthy or Sensitive Groups'
                    },
                    {
                        min: 55,
                        max: 154,
                        gaugeFill: 30,
                        description: 'Moderate'
                    },
                    {
                        min: 0,
                        max: 54,
                        gaugeFill: 19,
                        description: 'Good'
                    }
                ]
            },
            {
                type: 'O\u2083',
                name: 'Ozone',
                details: [
                    'Ozone is created in a chemical reaction between atmospheric oxygen, nitrogen oxides, organic compounds and sunlight',
                    'Irritate the airways causing coughing, a burning sensation, wheezing and shortness of breath',
                    'Children, people with respiratory or lung and heart diseases, elderly and those who exercise outdoors are particularly vulnerable to ozone exposure'
                ],
                unit: 'ppb',
                apiField: 'o3',
                qualityRanges: [
                    {
                        min: 201,
                        max: 9999,
                        gaugeFill: 100,
                        description: 'Hazardous'
                    },
                    {
                        min: 106,
                        max: 200,
                        gaugeFill: 80,
                        description: 'Very Unhealthy'
                    },
                    {
                        min: 86,
                        max: 105,
                        gaugeFill: 75,
                        description: 'Unhealthy'
                    },
                    {
                        min: 71,
                        max: 85,
                        gaugeFill: 50,
                        description: 'Unhealthy or Sensitive Groups'
                    },
                    {
                        min: 55,
                        max: 70,
                        gaugeFill: 30,
                        description: 'Moderate'
                    },
                    {
                        min: 0,
                        max: 54,
                        gaugeFill: 19,
                        description: 'Good'
                    }
                ]
            },
            {
                type: 'NO\u2082',
                name: 'Nitrogen dioxide',
                details: [
                    'Main sources are fuel burning processes in industry and transportation',
                    'Cause increased bronchial reactivity in patients with asthma, lung function decline in patients with COPD',
                    'Cause increased risk of respiratory infections, especially in young children'
                ],
                unit: 'ppb',
                apiField: 'no2',
                qualityRanges: [
                    {
                        min: 1650,
                        max: 2049,
                        gaugeFill: 100,
                        description: 'Hazardous'
                    },
                    {
                        min: 1250,
                        max: 1649,
                        gaugeFill: 90,
                        description: 'Hazardous'
                    },
                    {
                        min: 650,
                        max: 1249,
                        gaugeFill: 80,
                        description: 'Very Unhealthy'
                    },
                    {
                        min: 361,
                        max: 649,
                        gaugeFill: 75,
                        description: 'Unhealthy'
                    },
                    {
                        min: 101,
                        max: 360,
                        gaugeFill: 50,
                        description: 'Unhealthy or Sensitive Groups'
                    },
                    {
                        min: 54,
                        max: 100,
                        gaugeFill: 30,
                        description: 'Moderate'
                    },
                    {
                        min: 0,
                        max: 53,
                        gaugeFill: 19,
                        description: 'Good'
                    }
                ]
            },
            {
                type: 'SO\u2082',
                name: 'Sulfur dioxide',
                details: [
                    'Main sources are burning processes of sulfur-containing fuel in industry and electricity generation',
                    'Cause irritation to the respiratory tract, coughing',
                    'Generates an inflammatory reaction that may worsen the condition of patients with asthma and patients with pneumonia and bronchitis'
                ],
                unit: 'ppb',
                apiField: 'so2',
                qualityRanges: [
                    {
                        min: 805,
                        max: 1004,
                        gaugeFill: 100,
                        description: 'Hazardous'
                    },
                    {
                        min: 605,
                        max: 804,
                        gaugeFill: 90,
                        description: 'Hazardous'
                    },
                    {
                        min: 305,
                        max: 604,
                        gaugeFill: 80,
                        description: 'Very Unhealthy'
                    },
                    {
                        min: 186,
                        max: 304,
                        gaugeFill: 75,
                        description: 'Unhealthy'
                    },
                    {
                        min: 76,
                        max: 185,
                        gaugeFill: 50,
                        description: 'Unhealthy or Sensitive Groups'
                    },
                    {
                        min: 36,
                        max: 75,
                        gaugeFill: 30,
                        description: 'Moderate'
                    },
                    {
                        min: 0,
                        max: 35,
                        gaugeFill: 19,
                        description: 'Good'
                    }
                ]
            },
            {
                type: 'CO',
                name: 'Carbon monoxide',
                details: [
                    'CO is a product of incomplete combustion in car engines and industry',
                    'Cause dizziness, nausea and headaches',
                    'Pregnant women, populations with diseases that inhibit regular oxygen supply in the body (such as chronic bronchitis or anemia) and the elderly are especially susceptible'
                ],
                unit: 'ppb',
                apiField: 'co',
                qualityRanges: [
                    {
                        min: 40500,
                        max: 50400,
                        gaugeFill: 100,
                        description: 'Hazardous'
                    },
                    {
                        min: 30500,
                        max: 40400,
                        gaugeFill: 90,
                        description: 'Hazardous'
                    },
                    {
                        min: 15500,
                        max: 30400,
                        gaugeFill: 80,
                        description: 'Very Unhealthy'
                    },
                    {
                        min: 12500,
                        max: 15400,
                        gaugeFill: 75,
                        description: 'Unhealthy'
                    },
                    {
                        min: 9500,
                        max: 12400,
                        gaugeFill: 50,
                        description: 'Unhealthy or Sensitive Groups'
                    },
                    {
                        min: 4500,
                        max: 9400,
                        gaugeFill: 30,
                        description: 'Moderate'
                    },
                    {
                        min: 0,
                        max: 4400,
                        gaugeFill: 19,
                        description: 'Good'
                    }
                ]
            }
        ];
    }
}
